import type { ApiRequestConfig } from '@/shared/api/axios'
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import { toast } from 'sonner'
import { ApiErrorResponse, ApiResponse } from '../types'
import type { ManagedRequestConfig } from './axios.utils'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function unwrapApiResponse<T>(payload: ApiResponse<T> | T): T {
  if (!isRecord(payload) || payload.success !== true || !('statusCode' in payload)) {
    return payload as T
  }

  const response = payload as unknown as ApiResponse<T> & { meta?: unknown }

  if ('meta' in response && response.meta !== undefined) {
    return {
      data: response.data,
      meta: response.meta,
      message: response.message,
    } as T
  }

  if (response.data !== undefined) {
    return response.data as T
  }

  return response as T
}

export const apiMethodWrapper = (axiosInstance: AxiosInstance) => {
  return {
    // GET request
    get: async <T = unknown>(url: string, config?: ApiRequestConfig): Promise<T> => {
      const response: AxiosResponse<ApiResponse<T>> = await axiosInstance.get(url, config)
      return unwrapApiResponse(response.data)
    },

    // POST request
    post: async <T = unknown>(
      url: string,
      data?: unknown,
      config?: ApiRequestConfig,
    ): Promise<T> => {
      const response: AxiosResponse<ApiResponse<T>> = await axiosInstance.post(
        url,
        data,
        config,
      )
      return unwrapApiResponse(response.data)
    },

    // PUT request
    put: async <T = unknown>(
      url: string,
      data?: unknown,
      config?: ApiRequestConfig,
    ): Promise<T> => {
      const response: AxiosResponse<ApiResponse<T>> = await axiosInstance.put(
        url,
        data,
        config,
      )
      return unwrapApiResponse(response.data)
    },

    // PATCH request
    patch: async <T = unknown>(
      url: string,
      data?: unknown,
      config?: ApiRequestConfig,
    ): Promise<T> => {
      const response: AxiosResponse<ApiResponse<T>> = await axiosInstance.patch(
        url,
        data,
        config,
      )
      return unwrapApiResponse(response.data)
    },

    // DELETE request
    delete: async <T = unknown>(url: string, config?: ApiRequestConfig): Promise<T> => {
      const response: AxiosResponse<ApiResponse<T>> = await axiosInstance.delete(
        url,
        config,
      )
      return unwrapApiResponse(response.data)
    },
  }
}

export const apiErrorHandler = (error: AxiosError<ApiErrorResponse>) => {
  const { response: errorResponse } = error
  const { error: errorMessage, errors, message, statusCode } = errorResponse?.data ?? {}
  const resolvedMessage = message ?? errorMessage ?? 'An unknown error occurred'
  const requestConfig = error.config as ManagedRequestConfig | undefined

  if (!requestConfig?._skipToast) {
    toast.error(resolvedMessage)
  }

  return {
    error: resolvedMessage,
    message,
    statusCode,
    // Carried through so callers can pin each string to its own form field.
    ...(Array.isArray(errors) && errors.length > 0 ? { errors } : {}),
  }
}
